import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { mastersApi, fetchActiveUsers, userLabel } from '../../services/expenseMastersApi';

// Loads active lookup lists for dropdowns. `needs` picks which lists to fetch:
//   countries, currencies, categories, users, jobNames
const useExpenseLookups = (needs = []) => {
  const [lookups, setLookups] = useState({ countries: [], currencies: [], categories: [], users: [], jobNames: [] });
  const key = needs.join(',');

  useEffect(() => {
    let cancelled = false;
    const wanted = key.split(',').filter(Boolean);

    const loaders = {
      countries: () => mastersApi.list('countries', { limit: 500 }).then((r) =>
        (r.data || []).map((c) => ({ value: c._id, label: `${c.name} (${c.iso2})`, raw: c }))),
      currencies: () => mastersApi.list('currencies', { limit: 500 }).then((r) =>
        (r.data || []).map((c) => ({ value: c.code, label: `${c.code} - ${c.name}`, raw: c }))),
      categories: () => mastersApi.list('categories', { limit: 500 }).then((r) =>
        (r.data || []).map((c) => ({ value: c._id, label: c.name, raw: c }))),
      jobNames: () => mastersApi.list('jobNames', { limit: 500 }).then((r) =>
        (r.data || []).map((j) => ({ value: j._id, label: j.name, raw: j }))),
      users: () => fetchActiveUsers().then((users) =>
        (Array.isArray(users) ? users : []).map((u) => ({ value: u._id, label: userLabel(u), raw: u }))),
    };

    Promise.all(wanted.map((n) => loaders[n]().then((list) => [n, list]).catch((e) => {
      toast.error(`Failed to load ${n}: ${e.message}`);
      return [n, []];
    }))).then((entries) => {
      if (!cancelled) setLookups((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    });

    return () => { cancelled = true; };
  }, [key]);

  return lookups;
};

// Ensure the currently-referenced option is selectable when editing (it may be inactive)
export const withCurrent = (options, value, label) => {
  if (!value || options.some((o) => o.value === value)) return options;
  return [...options, { value, label: `${label || value} (inactive)` }];
};

export default useExpenseLookups;
